import React, { useState, useEffect } from 'react';
import axios from "axios";
import { 
  PlusIcon,
  CalendarIcon,
  BriefcaseIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import StatusToggleModal from './StatusToggleModal';
import toast from 'react-hot-toast';
import DeleteModal from '../../common/DeleteModal';
import CreateNoticeModal from './Createnoticemoda';
import { Pencil, PinIcon, Trash2 } from 'lucide-react';
import { useRef } from 'react';
import EditNoticeModal from './EditNoticeModal';

const CAT_STYLES = {
  General:     { bg: "#EAF3DE", text: "#3B6D11", dot: "#4ade80" },
  Tender:      { bg: "#ede9fe", text: "#5b21b6", dot: "#8b5cf6" },
  Recruitment: { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  Policy:      { bg: "#fce7f3", text: "#9d174d", dot: "#ec4899" },
  Holiday:     { bg: "#fef9c3", text: "#854d0e", dot: "#f59e0b" },
  Urgent:      { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
};

function NoticeCard({ notice, onEdit, onDelete, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();
  const cat = CAT_STYLES[notice.category] || CAT_STYLES.General;
  const isExpired = new Date(notice.expiry) < new Date();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
      style={{
        opacity: visible ? (isExpired ? 0.65 : 1) : 0,           // ✅ Fixed: merged opacity logic
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.5s ease ${index * 0.06}s, transform 0.5s ease ${index * 0.06}s, box-shadow 0.2s`,
        background: isExpired ? "#fafafa" : "#e0ead8",
        border: notice.pinned
          ? "1.5px solid #4ade80"
          : `1.5px solid ${isExpired ? "#e5e7eb" : "#e8f0e0"}`,
        boxShadow: notice.pinned
          ? "0 4px 20px rgba(74,222,128,0.12)"
          : "0 2px 10px rgba(0,0,0,0.04)",
        fontFamily: "'Barlow', sans-serif",
      }}
    >
      {notice.pinned && (
        <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full"
          style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)", left: "-1px" }} />
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full"
              style={{ background: cat.bg, color: cat.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: cat.dot }} />
              {notice.category}
            </span>
            {notice.pinned && (
              <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#fef9c3", color: "#92400e" }}>
                <PinIcon size={12} /> Pinned   {/* ✅ Fixed: added size prop */}
              </span>
            )}
            {isExpired && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: "#f3f4f6", color: "#9ca3af" }}>Expired</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-gray-400 text-xs flex-shrink-0">
            <CalendarIcon className="h-3.5 w-3.5" />   {/* ✅ Fixed: added size class */}
            {new Date(notice.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>

        <h3
          className="font-black text-gray-900 leading-snug mb-2 group-hover:text-green-700 transition-colors"
          style={{ fontSize: "clamp(14px, 1.5vw, 16px)" }}
        >
          {notice.title}
        </h3>

        <div className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4"
          dangerouslySetInnerHTML={{ __html: notice.content }}
        />

        <div className="flex items-center justify-end gap-3">
          {/* ✅ Fixed: wrapped in arrow functions so they don't fire on render */}
          <button
            onClick={() => onEdit(notice)}
            className="flex items-center justify-center w-9 h-9 rounded-xl 
            bg-green-50 text-green-600 border border-green-100 
            hover:bg-green-600 hover:text-white hover:shadow-md 
            transition-all duration-200"
          >
            <Pencil size={16} />
          </button>

          <button
            onClick={() => onDelete(notice)}
            className="flex items-center justify-center w-9 h-9 rounded-xl 
            bg-red-50 text-red-600 border border-red-100 
            hover:bg-red-600 hover:text-white hover:shadow-md 
            transition-all duration-200"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

const CarrierList = () => {
  const [allJobs, setAlljobs] = useState([]);
  const [loading, setloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [openCreateModal, setOpenCreatemodal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedJobForStatus, setSelectedJobForStatus] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const getAllNotice = async () => {
    try {
      setloading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notice/getAll`);
      setAlljobs(res.data.data);
    } catch (error) {
      console.log("Error - ", error);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    getAllNotice();
  }, []);


  const handleStatusChanged = async (updatedJob) => {
    getAllNotice();
    toast.success(`Notice status updated to ${updatedJob.isActive ? 'Active' : 'Inactive'} successfully!`);
  };

  // ✅ Fixed: onDelete now opens modal and stores ID instead of deleting immediately
  const handleDeleteClick = (notice) => {
    setDeleteId(notice._id);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setloading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/notice/delete/${deleteId}`); // 
      setAlljobs(prev => prev.filter(job => job._id !== deleteId));
      toast.success("Notice deleted successfully");
    } catch (error) {
      console.error("Error deleting notice:", error);
      toast.error("Delete failed");
    } finally {
      setloading(false);
      setOpenDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleEdit = (notice) => {
    setSelectedJob(notice);
    setIsEditModalOpen(true);
  };

  // ✅ Fixed: filter logic applied to notices (category/title instead of job fields)
  const filteredJobs = allJobs.filter(notice => {
    const matchesSearch =
      notice.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notice.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notice.content?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'all' ? true :
      filterStatus === 'active' ? !notice.expiry || new Date(notice.expiry) >= new Date() :
      filterStatus === 'inactive' ? new Date(notice.expiry) < new Date() : true;

    return matchesSearch && matchesFilter;
  });

  if (loading && allJobs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="relative inline-flex mb-4">
            <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 bg-amber-500 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="text-gray-700 text-lg font-medium animate-pulse">Loading notices...</p>
          <div className="flex justify-center gap-1 mt-2">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BriefcaseIcon className="h-8 w-8 text-blue-600" />
              Notice Management
            </h1>
            <p className="text-gray-600 mt-1">Manage all notices from one place</p>
          </div>
          <button
            onClick={() => setOpenCreatemodal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg shadow-blue-200"
          >
            <PlusIcon className="h-5 w-5" />
            Create New Notice
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, category, or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              />
            </div>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
              <FunnelIcon className="h-5 w-5 text-gray-500 ml-2" />
              {['all', 'active', 'inactive'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-all ${
                    filterStatus === status
                      ? `bg-white shadow-sm ${status === 'active' ? 'text-green-600' : status === 'inactive' ? 'text-red-600' : 'text-blue-600'}`
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden p-4">
          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notices found</h3>
              <p className="text-gray-500">Try adjusting your search or create a new notice</p>
            </div>
          ) : (
            // ✅ Fixed: use filteredJobs instead of allJobs
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredJobs.map((n, i) => (
                <NoticeCard
                  key={n._id}                      // ✅ Fixed: use _id (MongoDB) not n.id
                  notice={n}
                  index={i}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}     // ✅ Fixed: use handleDeleteClick
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateNoticeModal
        isOpen={openCreateModal}
        onClose={() => setOpenCreatemodal(false)}
        onSuccess={() => {
          setOpenCreatemodal(false);
          getAllNotice();
        }}
      />
      <StatusToggleModal
        isOpen={isStatusModalOpen}
        onClose={() => { setIsStatusModalOpen(false); setSelectedJobForStatus(null); }}
        onStatusChanged={handleStatusChanged}
        job={selectedJobForStatus}
      />
      <EditNoticeModal
        isOpen={isEditModalOpen}
        notice={selectedJob}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          setIsEditModalOpen(false);
          getAllNotice();
        }}
      />
      <DeleteModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onDelete={handleDelete}
        loading={loading}
      />
    </div>
  );
};

export default CarrierList;